import { IconChevronDown } from '@tabler/icons-react';
import { cn } from '@/lib/utils';
import getTextColor from '@/utils/getTextColorContrast';
import { Category, SelectedCategory } from '@/types';

interface CategoriesChartProps {
  fallbackBackgroundColor: string;
  sortedCategories: Category[];
  maxCount: number;
  handleShowCategoryDescription: (categoryId: string) => void;
  showCategoryDescription: string | null;
  onSelectCategory: (category: SelectedCategory) => void;
  isTally: boolean;
}

function CategoriesChart({
  fallbackBackgroundColor,
  sortedCategories,
  maxCount,
  handleShowCategoryDescription,
  showCategoryDescription,
  onSelectCategory,
  isTally,
}: CategoriesChartProps) {
  return (
    <div className="flex w-full max-w-[calc(100%-58px)]">
      <div className="flex flex-col w-full">
        {sortedCategories?.map((item: Category, index: number) => {
          const { id, label, count, color: itemColor, prompt, percentage } = item || {};
          const widthPercentage = (count / maxCount) * 100;
          const isEmpty = count === 0;
          const isCategoryDescriptionVisible = showCategoryDescription === id;
          const backgroundColor = isEmpty ? fallbackBackgroundColor : itemColor;
          const color = getTextColor(!isEmpty ? itemColor : fallbackBackgroundColor);
          const style = { backgroundColor, color };
          return (
            <div key={id} className="flex w-full gap-3">
              {/* Category label section */}
              <button
                type="button"
                onClick={() => handleShowCategoryDescription(id)}
                className={cn(
                  'flex text-left gap-3 text-[15px] max-w-[320px] w-full border-r min-h-7 pr-4',
                  index !== sortedCategories.length - 1 && 'pb-5',
                )}
              >
                {/* Category number section */}
                <span
                  style={style}
                  className="flex items-center justify-center w-7 h-7 font-semibold rounded-md flex-shrink-0"
                >
                  {index + 1}
                </span>
                <div className="flex w-full flex-col min-h-7">
                  <div className="flex w-full justify-between max-w-[264px] min-h-7">
                    <span className="font-medium max-w-[240px] break-words">{label}</span>
                    <IconChevronDown
                      size={20}
                      className={cn(
                        'transition-transform duration-300 ease-in-out',
                        isCategoryDescriptionVisible ? 'rotate-180' : 'rotate-0',
                      )}
                    />
                  </div>
                  {/* Category description section */}
                  <p
                    className={cn(
                      'overflow-hidden transition-all duration-300 ease-in-out text-xs pt-1 max-w-[240px]',
                      isCategoryDescriptionVisible ? 'max-h-32 opacity-100' : 'max-h-0 opacity-0',
                    )}
                  >
                    {prompt}
                  </p>
                </div>
              </button>

              {/* Chart bar section */}
              <div className="flex-1 flex w-full pl-2">
                <button
                  type="button"
                  onClick={() => onSelectCategory({ label, value: id })}
                  style={{
                    ...style,
                    width: `${widthPercentage}%`,
                    minWidth: isTally ? '3ch ' : '6.5ch',
                  }}
                  className="flex font-medium rounded-sm px-3 h-7 items-center"
                >
                  {isTally ? count : `${percentage?.toFixed(2)}%`}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default CategoriesChart;
